import { Store, TrendingUp, Package, CreditCard, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

const features = [
  {
    icon: Store,
    title: 'Reach Millions',
    description: 'Get your products in front of millions of fashion-conscious shoppers across India'
  },
  {
    icon: TrendingUp,
    title: 'Grow Your Business',
    description: 'Access powerful analytics and tools to help you scale your fashion business'
  },
  {
    icon: Package,
    title: 'Easy Logistics',
    description: 'We handle shipping, returns, and customer service so you can focus on your products'
  },
  {
    icon: CreditCard,
    title: 'Quick Payments',
    description: 'Get paid quickly with our secure and reliable payment system'
  }
];

const BecomeSellerPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/10 via-aurakart-yellow/10 to-aurakart-green/10 py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Become an <span className="text-gradient-rainbow">AuraKart</span> Seller
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Join India's fastest growing fashion marketplace and reach millions of customers
            </p>
            <a 
              href="https://partner.aurakartfashion.com/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="cta" size="xl">
                Start Selling Today
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            Why Sell on AuraKart?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-rainbow flex items-center justify-center">
                  <feature.icon className="h-8 w-8 text-primary-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-secondary/50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            Start Selling in 3 Easy Steps
          </h2>
          
          <div className="max-w-3xl mx-auto">
            {[
              { step: 1, title: 'Register', description: 'Create your seller account with basic details and documents' },
              { step: 2, title: 'List Products', description: 'Upload your products with images, descriptions, and pricing' },
              { step: 3, title: 'Start Selling', description: 'Go live and start receiving orders from customers across India' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-6 mb-8"
              >
                <div className="w-12 h-12 flex-shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://partner.aurakartfashion.com" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="cta" size="lg">
                Go to Partner Portal
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BecomeSellerPage;
